import { createFileRoute, stripSearchParams } from '@tanstack/react-router';

import { getLedgerAccountsQueryOptions } from '~/data/ledgerAccount';
import { getSavedFiltersQueryOptions } from '~/data/savedFilter';

import {
  LEDGER_ACCOUNTS_DEFAULT_FILTERS,
  LedgerAccountsPage,
  mapLedgerAccountsFiltersToPayload,
} from '~/modules/ledgerAccounts';
import { UNIVERSAL_FILTER_TYPES } from '~/modules/universalFilters';

import { i18n } from '~/utils';
import { buildRouteExport } from '~/utils/buildRouteExport';
import { createProtectedRoute } from '~/utils/routeProtection';
import { getRouteHead } from '~/utils/routeUtils';

import { routeParams } from './-routeParams';
import {
  defaultLedgerAccountsSearch,
  zodLedgerAccountsSearchSchema,
} from './-searchParams';

const title = i18n.t(($) => $.pageTitle, { ns: 'ledgerAccounts' });

export const Route = createFileRoute(
  '/(main)/settings/masterdata/ledger-accounts/',
)({
  beforeLoad: createProtectedRoute({
    routePath: '/settings/masterdata/ledger-accounts',
    ...routeParams,
  }),
  component: LedgerAccountsPage,
  head: () => getRouteHead(title),
  loader: async ({ context: { queryClient }, deps }) => {
    const { limit, page, savedFilterId } = deps;

    // Always prefetch the saved-filter tabs: the page needs them to resolve
    // the active draft before it can render the primary/drawer filters.
    void queryClient
      .query(
        getSavedFiltersQueryOptions({
          queryParams: {
            type: UNIVERSAL_FILTER_TYPES.ledgerAccounts,
          },
          options: {
            meta: {
              suppressErrorForErrorCodes: [422],
            },
          },
        }),
      )
      .catch(() => undefined);

    // Without a saved filter the initial draft is exactly the config's
    // `defaultFilters`, so the identical payload the table will request can
    // be derived here. With a `savedFilterId` the draft comes from the
    // server response and is not knowable yet, so the prefetch is skipped
    // rather than guessed at.
    if (savedFilterId) {
      return;
    }

    void queryClient
      .query(
        getLedgerAccountsQueryOptions({
          queryParams: {
            ...mapLedgerAccountsFiltersToPayload(
              LEDGER_ACCOUNTS_DEFAULT_FILTERS,
              '',
            ),
            limit,
            offset: page * limit,
          },
        }),
      )
      .catch(() => undefined);
  },
  loaderDeps: ({ search }) => search,
  search: {
    middlewares: [stripSearchParams(defaultLedgerAccountsSearch)],
  },
  staticData: {
    AppBar: {
      title,
    },
  },
  validateSearch: zodLedgerAccountsSearchSchema,
});

export const MASTERDATA_LEDGER_ACCOUNTS = buildRouteExport({
  Route,
  ...routeParams,
});
